import React, { useState, useEffect } from 'react';
import { Service, OverallKey, ServiceStatus } from '../types';
import { SERVICES, OVERALL_MESSAGES } from '../constants';
import { useServiceStatus } from '../hooks/useServiceStatus';
import ServiceCard from './ServiceCard';
import IncidentModal from './IncidentModal';
import Pulse from './Pulse';

export default function StatusPage() {
  const statuses = useServiceStatus();
  const [incident, setIncident] = useState<Service | null>(null);
  const [tick, setTick]         = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTick((p) => p + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const allStatuses = SERVICES.map((s) => statuses[s.id]?.status);
  const online   = allStatuses.filter((s) => s === 'online').length;
  const degraded = allStatuses.filter((s) => s === 'degraded').length;
  const offline  = allStatuses.filter((s) => s === 'offline').length;

  const overallStatus: OverallKey =
    offline  > 0 ? 'partial'  :
    degraded > 0 ? 'degraded' :
    allStatuses.every((s) => s === 'online') ? 'all_good' :
    'checking';

  const overall    = OVERALL_MESSAGES[overallStatus];
  const nextCheck  = 30 - (tick % 30);
  const pulseStatus = overallStatus === 'all_good' ? 'online' : overallStatus === 'checking' ? 'checking' : 'offline';

  const fallback: ServiceStatus = { status: 'checking', latency: null, lastChecked: null, history: [] };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600;700&family=IBM+Plex+Sans:wght@300;400;600&display=swap');

        @keyframes ping   { 0%   { transform: scale(1);   opacity: 0.4; } 100% { transform: scale(2.5); opacity: 0; } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes scan   { 0%   { transform: translateY(-100%); }          100% { transform: translateY(100vh); } }

        .service-card          { transition: all 0.2s ease; }
        .service-card:hover    { transform: translateY(-1px); border-color: rgba(168,255,120,0.2) !important; }
        .offline-card:hover    { border-color: rgba(255,68,68,0.3) !important; }
        .report-btn            { transition: all 0.15s ease; }
        .report-btn:hover      { background: rgba(255,68,68,0.15) !important; color: #ff6666 !important; }
      `}</style>

      {incident && <IncidentModal service={incident} onClose={() => setIncident(null)} />}

      <div style={{ minHeight: '100vh', background: '#080a0f', fontFamily: "'IBM Plex Sans', sans-serif", color: '#fff', position: 'relative', overflow: 'hidden' }}>

        {/* Scan line */}
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, height: '2px',
          background: 'linear-gradient(90deg, transparent, rgba(168,255,120,0.08), transparent)',
          animation: 'scan 8s linear infinite', pointerEvents: 'none', zIndex: 1,
        }} />

        {/* Grid background */}
        <div style={{
          position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
          backgroundImage: 'linear-gradient(rgba(168,255,120,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(168,255,120,0.025) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }} />

        <div style={{ position: 'relative', zIndex: 2, maxWidth: '860px', margin: '0 auto', padding: '2rem 1rem 4rem' }}>

          {/* Header */}
          <div style={{ marginBottom: '3rem', animation: 'fadeIn 0.6s ease' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem' }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#a8ff78', boxShadow: '0 0 8px #a8ff78' }} />
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.7rem', letterSpacing: '0.2em', color: '#a8ff78', textTransform: 'uppercase' }}>
                Loop Systems
              </span>
            </div>
            <h1 style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 'clamp(1.6rem, 4vw, 2.5rem)', fontWeight: 700, lineHeight: 1.1, marginBottom: '0.3rem' }}>
              Status Dashboard
            </h1>
            <p style={{ color: '#555', fontSize: '0.85rem' }}>Real-time monitoring · Refreshes every 30s</p>
          </div>

          {/* Overall banner */}
          <div style={{
            background: overallStatus === 'all_good' ? 'rgba(168,255,120,0.06)' : overallStatus === 'checking' ? 'rgba(80,80,80,0.08)' : 'rgba(255,68,68,0.06)',
            border: `1px solid ${overallStatus === 'all_good' ? 'rgba(168,255,120,0.2)' : overallStatus === 'checking' ? 'rgba(80,80,80,0.2)' : 'rgba(255,68,68,0.2)'}`,
            borderRadius: '12px', padding: '1.25rem 1.5rem', marginBottom: '2rem',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem',
            animation: 'fadeIn 0.6s ease 0.1s both',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <Pulse status={pulseStatus} />
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '1rem', fontWeight: 700, color: overall.color }}>
                {overall.text}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '1.5rem' }}>
              {([
                { label: 'Online',   value: online,   color: '#a8ff78' },
                { label: 'Degraded', value: degraded, color: '#ffcc44' },
                { label: 'Offline',  value: offline,  color: '#ff4444' },
              ] as const).map(({ label, value, color }) => (
                <div key={label} style={{ textAlign: 'center' }}>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '1.3rem', fontWeight: 700, color: value > 0 ? color : '#333' }}>
                    {value}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#555', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    {label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Service list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', animation: 'fadeIn 0.6s ease 0.2s both' }}>
            {SERVICES.map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
                status={statuses[service.id] ?? fallback}
                onReport={setIncident}
              />
            ))}
          </div>

          {/* Footer */}
          <div style={{ marginTop: '2.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.75rem', color: '#333', fontFamily: "'IBM Plex Mono', monospace" }}>
              Next check in <span style={{ color: '#555' }}>{nextCheck}s</span>
            </span>
            <span style={{ fontSize: '0.75rem', color: '#333' }}>
              {new Date().toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>

        </div>
      </div>
    </>
  );
}
